import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { GET_CIRCUITS_URL, CIRCUIT_URL, MAKE_CIRCUIT_URL, SWITCH_CIRCUIT_URL, VOUCHERS_CIRCUIT_URL } from "../../settings/APISetting";
import { arrayFilter, output } from "../../settings/themeSettings"; // eslint-disable-line no-unused-vars
import { applyStatusToList } from "../../utils/statusHelper";
import api from "../../config/axiosConfig";

export const fetchCircuits = createAsyncThunk("fetch-circuits", async (filters) => {
    const params = new URLSearchParams();

    // Gestion des différents types de paramètres
    if (filters) {
        if (typeof filters === 'string') {
            // Si c'est une string, c'est le type de dossier
            params.append('type', filters);
        } else if (typeof filters === 'object') {
            // Si c'est un object, ce sont des filtres de date/cloture
            if (filters.startDate) params.append('startDate', filters.startDate);
            if (filters.endDate) params.append('endDate', filters.endDate);
            if (filters.cloture !== undefined) params.append('cloture', filters.cloture);
            if (filters.type) params.append('type', filters.type);
        }
    }

    // Utiliser GET_CIRCUITS_URL (/api/get/circuits) qui sérialise avec le groupe Gcircuit
    // => retourne toutes les données : pax, guide, vehicule, transferts, excursions, toClient complet
    const response = await api.get(GET_CIRCUITS_URL, { params });
    return response.data;
});

// Utilitaire pour extraire l'id proprement
const getId = (obj) => {
    if (!obj) return null;
    if (typeof obj === 'object') return obj.id ?? null;
    return obj;
};

// Utilitaire pour formater une date (évite les null fatals)
const safeDate = (val) => {
    if (!val) return '';
    if (val && typeof val.toISOString === 'function') {
        return val.toISOString().split('.')[0] + 'Z';
    }
    return val;
};

// Construction du payload nettoyé
const buildCircuitPayload = (formData) => {
    // Extraire toClient : envoyer l'ID si disponible, sinon le code
    const toClientVal = formData.toClient
        ? (formData.toClient.id ?? formData.toClient.code ?? formData.toClient)
        : null;

    // Extraire programme : envoyer l'ID
    const programmeVal = formData.programme
        ? (formData.programme.id ?? formData.programme)
        : null;

    return {
        ...(formData.id ? { id: formData.id } : {}),
        // Champs simples
        reference: formData.reference,
        type: formData.type,
        pax: parseInt(formData.pax, 10) || 0,
        nombreJour: parseInt(formData.nombreJour, 10) || 0,
        isGroup: formData.isGroup ?? formData.grouped ?? false,
        grouped: formData.grouped ?? formData.isGroup ?? false,
        methodePaiement: formData.methodePaiement,
        commentaire: formData.commentaire ?? null,
        nomClient: formData.nomClient ?? null,
        numeroFacture: formData.numeroFacture ?? null,

        // Relations -> uniquement l'ID (ou code pour toClient)
        programme: programmeVal,
        toClient: toClientVal,
        chauffeur: getId(formData.chauffeur),
        guide: getId(formData.guide),
        vehicule: getId(formData.vehicule),

        // Dates sécurisées
        date: safeDate(formData.date),
        dateDebut: safeDate(formData.dateDebut),
        dateFin: safeDate(formData.dateFin),
        dateVolArrivee: safeDate(formData.dateVolArrivee),
        dateVolDepart: safeDate(formData.dateVolDepart),
        heureVolArrivee: safeDate(formData.heureVolArrivee),
        heureVolDepart: safeDate(formData.heureVolDepart),

        // Vols
        numeroVolArrivee: formData.numeroVolArrivee ?? null,
        numeroVolDepart: formData.numeroVolDepart ?? null,
        compagnieVolArrivee: formData.compagnieVolArrivee ?? null,
        compagnieVolDepart: formData.compagnieVolDepart ?? null,

        // Tableaux - On filtre les lignes vides pour éviter les erreurs 400 du backend
        hotels: (formData.hotels ?? [])
            .filter(h => h.hotel) // Un hôtel doit être sélectionné
            .map(h => ({
                ...h,
                hotel: getId(h.hotel),
                dateDebut: safeDate(h.dateDebut),
                dateFin: safeDate(h.dateFin),
                pax: parseInt(h.pax, 10) || 0,
                prixUnitaire: parseFloat(h.prixUnitaire) || 0
            })),

        excursions: (formData.excursions ?? [])
            .filter(e => e.excursion || e.type) // Une excursion doit être sélectionnée
            .map(e => ({
                ...e,
                excursion: getId(e.excursion) || getId(e.type),
                chauffeur: getId(e.chauffeur),
                guide: getId(e.guide),
                date: safeDate(e.date),
                pax: parseInt(e.pax, 10) || 0,
                prixUnitaire: parseFloat(e.prixUnitaire) || 0
            })),

        rooming: (formData.rooming ?? [])
            .filter(r => r.nom || r.libelle) // Un nom ou libellé est requis
            .map(r => ({
                ...r,
                pax: parseInt(r.pax, 10) || 0
            })),

        frais: (formData.frais ?? [])
            .filter(f => f.type && (parseFloat(f.montant) > 0)) // Type et montant requis
            .map(f => ({
                ...f,
                type: getId(f.type)
            })),

        transferts: (formData.transferts ?? [])
            .filter(t => t.depart || t.lieuDepart) // Un départ est requis
            .map(t => ({
                ...t,
                chauffeur: getId(t.chauffeur),
                vehicule: getId(t.vehicule),
                guide: getId(t.guide),
                date: safeDate(t.date),
                depart: t.lieuDepart || t.depart,
                arrivee: t.lieuArrivee || t.arrivee,
                pax: parseInt(t.pax, 10) || 0,
                prixUnitaire: parseFloat(t.prixUnitaire) || 0
            })),

        locations: (formData.locations ?? [])
            .filter(l => l.agence) // Une agence est requise
            .map(l => ({
                ...l,
                agence: getId(l.agence),
                chauffeur: getId(l.chauffeur),
                guide: getId(l.guide),
                vehicule: getId(l.vehicule),
                dateDebut: safeDate(l.dateDebut),
                dateFin: safeDate(l.dateFin),
                nombreJour: parseInt(l.nombreJour, 10) || 0,
                prixUnitaire: parseFloat(l.prixUnitaire) || 0
            })),

        envelops: (formData.envelops ?? [])
            .filter(e => e.frais && (parseFloat(e.montantEstime) > 0)) // Désignation et montant requis
            .map(e => ({
                frais: e.frais,
                montant: parseFloat(e.montantEstime) || 0,
                depouillement: parseFloat(e.montantReel) || 0,
            }))
    };
};

export const createCircuit = async (circuit, onError, onSuccess) => {
    try {
        const payload = buildCircuitPayload(circuit);
        console.log('[DEBUG] Création circuit - URL:', MAKE_CIRCUIT_URL);
        console.log('[DEBUG] Payload envoyé (nettoyé):', payload);

        const response = await api.post(MAKE_CIRCUIT_URL, payload, {
            headers: { 'Content-Type': 'application/ld+json' }
        });

        console.log('[DEBUG] Réponse serveur:', response);

        if (onSuccess) {
            onSuccess(response.data);
        }
    } catch (error) {
        console.error('[ERROR] Erreur création circuit:', error);
        // Afficher le message d'erreur détaillé du backend
        if (error.response) {
            console.error('[ERROR] Status:', error.response.status);
            console.error('[ERROR] Message backend:', JSON.stringify(error.response.data));
        }
        if (onError) {
            onError(error.response || { status: 0, data: { error: error.message } });
        }
    }
}

export const switchCircuit = async (id) => {
    return await api.get(`${SWITCH_CIRCUIT_URL}/${id}`);
}

// Nouvelle fonction pour la pré-clôture par l'agent de production
export const preCloseCircuit = async (id) => {
    return await api.patch(`${SWITCH_CIRCUIT_URL}/pre-cloture/${id}`);
}

// Nouvelle fonction pour la clôture définitive par l'administrateur
export const finalCloseCircuit = async (id) => {
    return await api.patch(`${SWITCH_CIRCUIT_URL}/cloture-definitive/${id}`);
}

// Nouvelle fonction pour rouvrir un dossier en pré-clôture (admin uniquement)
export const reopenPreClosedCircuit = async (id) => {
    return await api.patch(`${SWITCH_CIRCUIT_URL}/rouvrir/${id}`);
}

export const fetchCircuit = async (id) => {
    return await api.get(`${CIRCUIT_URL}/${id}`);
}

export const fetchVouchers = async (id) => {
    return await api.get(`${VOUCHERS_CIRCUIT_URL}/${id}`);
}





const circuitSlice = createSlice({

    name: 'circuits',
    initialState: { circuits: [], allCircuits: [], fetchStatus: '' },

    reducers: {
        filterCircuits: (state, action) => {
            const { nail = '', clientId = null } = action.payload || {};
            let result = state.allCircuits;

            // 1. Filtre par client sélectionné dans l'Autocomplete
            if (clientId) {
                result = result.filter(item => {
                    const clientObj = item.toClient || item.client;
                    return clientObj && clientObj.id === clientId;
                });
            }

            // 2. Filtre texte libre : cherche dans tous les champs + client.libelle
            if (nail && nail.trim()) {
                const regex = new RegExp(nail, 'i');
                result = result.filter(item => {
                    // Recherche dans les champs primitifs
                    for (let prop in item) {
                        const val = item[prop];
                        if (val !== null && val !== undefined && typeof val !== 'object') {
                            if (regex.test(String(val))) return true;
                        }
                    }
                    // Recherche dans le nom du client (objet imbriqué ou string)
                    const clientObj = item.toClient || item.client;
                    if (clientObj) {
                        if (typeof clientObj === 'string') {
                            if (regex.test(clientObj)) return true;
                        } else {
                            if ((clientObj.libelle && regex.test(clientObj.libelle)) || 
                                (clientObj.nomComplet && regex.test(clientObj.nomComplet)) ||
                                (clientObj.nom && regex.test(clientObj.nom))) {
                                return true;
                            }
                        }
                    }
                    return false;
                });
            }

            state.circuits = result;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(fetchCircuits.fulfilled, (state, action) => {
            // Appliquer la logique de statut à tous les circuits
            const processed = applyStatusToList(action.payload.reverse());
            state.circuits = processed;
            state.allCircuits = processed; // Garder une copie pour le filtrage
            state.fetchStatus = 'success';
        }).addCase(fetchCircuits.pending, (state, action) => {
            state.fetchStatus = 'loading';

        }).addCase(fetchCircuits.rejected, (state, action) => {

            state.fetchStatus = 'error';

        });
    }


});
export default circuitSlice;